---
title: "Free PDF tools with no sign-up: what can run in your browser"
description: "A practical map of pdfy tools: convert, compress, rotate, protect and split PDF files without accounts, watermarks or artificial waiting gates."
locale: "en"
date: "2026-06-29"
tags: ["pdf", "tools", "local"]
relatedTools: ["povernut", "vJpg", "jpgVPdf", "szhat", "splitPdf", "deletePdfPages", "watermark-pdf", "protect-pdf", "excelVPdf"]
translations:
  ru: "besplatnye-pdf-instrumenty-bez-registracii"
faq:
  - q: "Do all PDF tools run locally?"
    a: "Yes. Rotation, compression, conversion and page editing run in the browser, and the file is not uploaded."
  - q: "Do I need an account or paid plan?"
    a: "No. pdfy does not add sign-up, watermarks, waiting timers or paid gates."
---

An upload form rejects a PDF over 5 MB, a scan is sideways, and a contract still
has two pages that should be removed before sharing. In those jobs the annoying
part is often not the operation itself, but the hidden condition: sign-up,
watermark, waiting timer, download limit or an upload where the page promised
privacy.

[pdfy.ru](/) keeps all PDF tools free with no account, watermark or artificial
waiting gate. All of them run right in your browser, so the file stays on your
device.

## What "free" means for PDF tools

| Task | Where it runs | Account | Service watermark | Best use |
| --- | --- | --- | --- | --- |
| [Rotate PDF](/povernut), [split PDF](/razdelit-pdf), [delete pages](/udalit-stranicy-iz-pdf) | Browser | No | No | Quick page and scan fixes |
| [PDF to JPG](/v-jpg), [JPG to PDF](/jpg-v-pdf) | Browser | No | No | Previews, photos, sending images as one file |
| [Compress PDF](/szhat) | Browser | No | No | Shrinking a file before email or a portal upload |
| [Watermark PDF](/vodyanoj-znak-pdf), [protect PDF](/postavit-parol-na-pdf) | Browser | No | No | User-owned marking and protecting working documents |
| [Excel to PDF](/excel-v-pdf), [HTML to PDF](/html-v-pdf) | Browser | No | No | Tables and web layouts to send as one file |

The promise that files never leave the device is easy to verify in DevTools: the
source file should not appear in network requests.

## What the browser cannot do

Word and PowerPoint need an engine that calculates fonts, line breaks, print
areas and slides. That is why pdfy does not convert them: save the document as
PDF in the editor. OCR for scans is not part of the set either.

Browsers read Excel spreadsheets well, but without styling: cell values are
carried over, colors and merged cells are not.

## What to check before you start

Before working with a document, check three things: whether an account is
required, whether a watermark will be added, and where the file will be
processed. If the task itself is unclear, use the separate guide on
[choosing a PDF converter](/blog/which-pdf-converter-to-use) instead of guessing
from one upload box.

Start from the specific tool page rather than a universal upload form. It makes
the limits and expected output visible before you spend time on it.
